import * as React from 'react';
import { useState } from "react";

// Components
import { Props as PLProps, PubListGenerator } from '../components/PubListGenerator';
import { PubListConfigInternal } from '../types';

// API
import { PublistServiceApi } from '../../api/publist/apis';
import {
  PubListConfig as APIConfig,
  PubListConfigListTypeEnum,
  PublistConfigProjectOrganisationDeterminationTypeEnum,
  PublistConfigProjectOrganisationFilterMembersEnum,
} from '../../api/publist/models';
import { TextApiResponse } from '../../api/publist/runtime';

// Misc
import { connectOAuth2, OAuth2AwareProps } from '../../Auth/utils/connect';
import { defineMessages, injectIntl, WrappedComponentProps as IntlProps } from 'react-intl';

import getLogger from '../../log';
import { getVocabularyAsArray } from '../../Vocabulary/util';
import { cslLocaleList, initialCslLocale } from '../data';
import { getLanguageFromLocale, Language } from '../../Intl/utils';
import { useStatusCode } from 'Utils/StatusCodeHandler';
import { handleStatusCode422 } from 'Utils/StatusCodeUtils';
const logger = getLogger('PubListGenerator/container');

const viewMessageTimeout = 3000;

const messages = defineMessages({
  pubListConfigSaved: { id: 'publist.message.publistSaved' },
  pubListConfigSavedNew: { id: 'publist.message.publistSavedNew' },
  publistNoResult: { id: 'publist.message.publistNoResult' },
});

export interface Props {
  config?: PubListConfigInternal;
  apiKey?: string;
}

type AwareProps = OAuth2AwareProps<Props> & IntlProps;

function ConnectedInnerPubListGenerator(props: AwareProps) {
  const languageLocal: Language = getLanguageFromLocale(props.intl.locale);
  const cslLocales: { abbr: string; name: string }[] = getVocabularyAsArray(cslLocaleList, languageLocal, '*');
  const [assetsAvailable, setAssetsAvailable] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [emptyList, setEmptyList] = useState<boolean>(false);
  const [apiKey, setApiKey] = useState<string>(props.apiKey ? props.apiKey : null);
  const [config, setConfig] = useState<PubListConfigInternal>(
    props.config !== null
      ? props.config
      : {
          listType: PubListConfigListTypeEnum.PUBLICATION,
          projectOrganisationConfig: {
            projectIds: [],
            organisationIds: [],
            withOrganisationHierarchy: false,
            considerEmploymentPeriod: false,
            determinationType: PublistConfigProjectOrganisationDeterminationTypeEnum.MEMBERS,
            filterMembers: PublistConfigProjectOrganisationFilterMembersEnum.ALL,
            restrictToRoles: [],
            excludeByRoles: [],
          },
          filterByYear: [],
          filterByCreationDate: [],
          filterByType: [],
          excludeByType: [],
          filterByPr: undefined,
          filterByLang: [],
          filterByRole: [],
          filterByRoleExcl: [],
          filterPersonByRole: [],
          groupByYear: false,
          groupByType: false,
          pubsort: 'stm',
          toc: false,
          locale: cslLocales.filter((lang: { abbr: string; name: string }) => lang.abbr === initialCslLocale)[0],
          linking: [],
          replacements: [],
          personIds: [],
          filterIds: [],
          filterPersons: [],
          keyIds: [],
          style: 'ieee',
          useMainName: true,
        }
  );
  const [viewMessage, setViewMessage] = useState<string>();
  const [preview, setPreview] = useState<string>();
  const api = props.apiWithAuth(new PublistServiceApi());
  const { setStatusCode } = useStatusCode();

  const getRenderProps = (): PLProps => {
    return {
      assetsAvailable,
      preview,
      config,
      viewMessage,
      loading,
      emptyList,

      onSuccess: () => { window.scrollTo({top: 0, left: 0, behavior: 'smooth'}); },
      loadPreview: boundHandleLoadPreview,
      storeConfig: apiKey ? boundHandleStoreConfig : null,
      storeNewConfig: boundHandleStoreNewConfig,
      updateConfig: boundHandleUpdateConfig,
      alertShow: (message) => {
        alert(message);
      },
      clearAllInput
    };
  };

  const boundHandleLoadPreview = async (config: any) => {
    setLoading(true);
    const cfg: APIConfig = {};
    for (const k in config) {
      if (!config.hasOwnProperty(k)) {
        continue;
      }
      // Skip empty array parameters
      if (config[k] instanceof Array && config[k].length === 0) {
        continue;
      }
      cfg[k] = config[k];
      setAssetsAvailable(false);
    }

    api
      .previewRaw({ pubListConfig: cfg })
      .then((response: TextApiResponse) => {
        if (response.raw.status === 200) {
          return response.value();
        } else if (response.raw.status === 404) {
          throw new Error(`Publist service not available. Please contact the administrator.`);
        } else if (response.raw.status === 422) {
          throw new Error(`Your config is not valid and results in an empty list`);
        }
        throw new Error(
          `Status Code ${response.raw.status}:\n` +
            `A server error occured. Please try again or contact the administrator.`
        );
      })
      .then((publist?: string) => {
        if (publist == '<h1>Empty list</h1>') {
          setLoading(false);
          setEmptyList(true);
          setPreview("<h1 class='empty-list'>" + props.intl.formatMessage(messages.publistNoResult) + "</h1>");
          setAssetsAvailable(true);
        } else {
          setLoading(false);
          setEmptyList(false);
          setPreview(publist);
          setAssetsAvailable(true);
        }
      })
      .catch((error) => {
        setLoading(false);
        setPreview('');
        setAssetsAvailable(true);
        if (error.status === 422) return handleStatusCode422(error, setStatusCode);
        setStatusCode({status: error.status});
      });
    // const response: TextApiResponse = await this.api.previewPublistRaw({publistConfig: cfg});
  };

  const boundHandleStoreConfig = async (config: any): Promise<number> => {
    setLoading(true);
    try {
      const response = await api.storeRaw({ pubListConfig: config, apiKey });
      setStatusCode({
        status: response.raw.status,
        action: 'update',
        data: {
          entity: 'publist',
          name: config.name === '' || config.name === undefined || config.name === null ? '' : '"' + config.name + '"',
          id: apiKey,
        },
      });
      setLoading(false);
      return response.raw.status;
    } catch (error) {
      setLoading(false);
      if (error.status === 422) {
        await handleStatusCode422(error, setStatusCode);
      } else {
        setStatusCode({ status: error.status });
      }
      return error.status;
    }
  };

  const boundHandleStoreNewConfig = async (config: any): Promise<number> => {
    setLoading(true);
    try {
      const response = await api.storeNewRaw({ pubListConfig: config });
      const apiKey = await response.value();
      setApiKey(apiKey);
      setStatusCode({
        status: response.raw.status,
        action: 'insert',
        data: {
          entity: 'publist',
          name: config.name === '' || config.name === undefined || config.name === null ? '' : '"' + config.name + '"',
          id: apiKey,
        },
      });
      setLoading(false);
      return response.raw.status;
    } catch (error) {
      setLoading(false);
      if (error.status === 422) {
        await handleStatusCode422(error, setStatusCode);
      } else {
        setStatusCode({ status: error.status });
      }
      return error.status;
    }
  };

  const boundHandleUpdateConfig = async (config: PubListConfigInternal) => {
    setConfig(config);
    setAssetsAvailable(true);
  };

  const clearAllInput = (listType: PubListConfigListTypeEnum) => {
    setConfig(
      {
        listType,
        projectOrganisationConfig: {
          projectIds: [],
          organisationIds: [],
          withOrganisationHierarchy: false,
          considerEmploymentPeriod: false,
          determinationType: PublistConfigProjectOrganisationDeterminationTypeEnum.MEMBERS,
          filterMembers: PublistConfigProjectOrganisationFilterMembersEnum.ALL,
          restrictToRoles: [],
          excludeByRoles: [],
        },
        filterByYear: [],
        filterByCreationDate: [],
        filterByType: [],
        excludeByType: [],
        filterByPr: undefined,
        filterByLang: [],
        filterByRole: [],
        filterByRoleExcl: [],
        filterPersonByRole: [],
        groupByYear: false,
        groupByType: false,
        pubsort: 'stm',
        toc: false,
        locale: cslLocales.filter((lang: { abbr: string; name: string }) => lang.abbr === initialCslLocale)[0],
        linking: [],
        replacements: [],
        personIds: [],
        filterIds: [],
        filterPersons: [],
        keyIds: [],
        style: 'ieee',
        useMainName: true,
        name: null
      }
    );
    setPreview('');
  };

  const _props = getRenderProps();

  return React.createElement(PubListGenerator, _props);
}

export const ConnectedPubListGenerator = connectOAuth2(injectIntl(ConnectedInnerPubListGenerator));

export default ConnectedPubListGenerator;
